var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState } from "react";
import { Flex, Card, Text, Box, tabListBehavior, Menu, ArrowRightIcon, ArrowLeftIcon, Button, ProviderConsumer as FluentUIThemeConsumer, } from "@fluentui/react-northstar";
import { DashboardCallout } from "./DashboardCallout";
import { Chart } from "../Chart/Chart";
import { Placeholder } from "./Placeholder";
import { getText } from "../../translations";
import { DescriptionList } from "./DescriptionList";
/**
 * The widget’s target size in the Dashboard’s grid layout.
 * @public
 */
export var EWidgetSize;
(function (EWidgetSize) {
    /**
     * The widget will occupy 1×1 grid cells.
     */
    EWidgetSize["Single"] = "single";
    /**
     * The widget will occupy 2×1 grid cells.
     */
    EWidgetSize["Double"] = "double";
    /**
     * The widget will occupy 3×1 grid cells.
     */
    EWidgetSize["Triple"] = "triple";
    /**
     * The widget will occupy 2×2 grid cells.
     */
    EWidgetSize["Box"] = "box";
})(EWidgetSize || (EWidgetSize = {}));
export var Widget = function (_a) {
    var id = _a.id, size = _a.size, body = _a.body, bodyByFilter = _a.bodyByFilter, link = _a.link, title = _a.title, desc = _a.desc, widgetActionGroup = _a.widgetActionGroup, widgetFilterGroup = _a.widgetFilterGroup, initialFilter = _a.initialFilter, hideWidget = _a.hideWidget, onInteraction = _a.onInteraction;
    var cardStyle = {
        gridColumnEnd: "auto",
        gridRowEnd: "auto",
        "@media (max-width: 842px)": {
            gridColumnEnd: "span 3",
        },
    };
    if (size === EWidgetSize.Double) {
        cardStyle.gridColumnEnd = "span 2";
    }
    if (size === EWidgetSize.Box) {
        cardStyle.gridColumnEnd = "span 2";
        cardStyle.gridRowEnd = "span 2";
    }
    if (size === EWidgetSize.Triple) {
        cardStyle.gridColumnEnd = "span 3";
    }
    var _b = useState(0), activeTabId = _b[0], setActiveTabId = _b[1];
    var _c = useState(widgetFilterGroup && bodyByFilter
        ? initialFilter || widgetFilterGroup[0].id
        : null), activeFilter = _c[0], setActiveFilter = _c[1];
    var activeBody = (activeFilter && bodyByFilter && bodyByFilter[activeFilter]) || body;
    return (React.createElement(Card, { styles: cardStyle, fluid: true },
        React.createElement(FluentUIThemeConsumer, { render: function (globalTheme) {
                var _a = globalTheme.siteVariables, t = _a.t, rtl = _a.rtl;
                return (React.createElement(React.Fragment, null,
                    React.createElement(Card.Header, null,
                        React.createElement(Flex, { gap: "gap.small", styles: { minHeight: "2rem", marginBlockStart: "-.5rem" } },
                            React.createElement(Flex, { gap: "gap.small", column: true, styles: { marginBlockStart: ".25rem" } },
                                React.createElement(Text, { content: getText(t.locale, title), style: { margin: 0 }, weight: "bold" }),
                                desc && (React.createElement(Text, { content: getText(t.locale, desc), size: "small" }))),
                            React.createElement(Box, { role: "none", styles: { flex: "1 0 0" } }),
                            activeFilter && (React.createElement(DashboardCallout, __assign({}, {
                                widgetId: id,
                                globalTheme: globalTheme,
                                calloutType: "filter",
                                widgetCalloutGroup: widgetFilterGroup,
                                setActiveFilter: setActiveFilter,
                                activeFilter: activeFilter,
                                t: t,
                                onInteraction: onInteraction,
                            }))),
                            React.createElement(DashboardCallout, __assign({}, {
                                widgetId: id,
                                globalTheme: globalTheme,
                                widgetCalloutGroup: widgetActionGroup,
                                hideWidget: hideWidget,
                                t: t,
                                onInteraction: onInteraction,
                            })))),
                    React.createElement(Card.Body, { style: {
                            marginBottom: "0.75rem",
                            height: "100%",
                            overflow: "hidden",
                        }, fitted: true }, activeBody ? (React.createElement(React.Fragment, null,
                        activeBody.length > 1 && (React.createElement(Menu, { style: {
                                border: "none",
                                background: "none",
                                marginBottom: "1.25rem",
                            }, items: Array.from(activeBody, function (_a) {
                                var id = _a.id, title = _a.title;
                                return Object.assign({
                                    key: id,
                                    content: getText(t.locale, title),
                                });
                            }), activeIndex: activeTabId, onItemClick: function (_a, props) {
                                var currentTarget = _a.currentTarget;
                                return setActiveTabId(props && props.index ? props.index : 0);
                            }, accessibility: tabListBehavior, underlined: true, primary: true })),
                        activeBody.map(function (_a, i) {
                            var id = _a.id, content = _a.content;
                            return (React.createElement(Flex, { key: id, styles: {
                                    height: "100%",
                                    display: activeTabId === i ? "flex" : "none",
                                }, column: true }, (function () {
                                switch (content.type) {
                                    case "chart":
                                        return (React.createElement(Chart, __assign({ key: "widget-chart__".concat(activeFilter || "", "__").concat(i) }, content.chart)));
                                    case "dl":
                                        return (React.createElement(DescriptionList, { t: t, list: content
                                                .list }));
                                    case "placeholder":
                                        return (React.createElement(Placeholder, { message: content
                                                .message }));
                                    case "text":
                                        return (React.createElement(Text, { as: "p", styles: {
                                                marginBlockStart: 0,
                                                marginBlockEnd: 0,
                                            } }, getText(t.locale, content.text)));
                                }
                            })()));
                        }))) : (React.createElement(EmptyState, { borderColor: globalTheme.siteVariables.colors.grey["300"] }))),
                    link && (React.createElement(Card.Footer, { fitted: true },
                        React.createElement(Flex, { space: "between", vAlign: "center" }, link.hasOwnProperty("href") ? (React.createElement(Text, { as: "a", href: link.href, target: "_blank", size: "small", color: "brand", styles: {
                                textDecoration: "none",
                                "&:focus": {
                                    outlineColor: globalTheme.siteVariables.colorScheme.default
                                        .foregroundActive,
                                },
                            } },
                            link.title
                                ? getText(t.locale, link.title)
                                : t["view more"],
                            rtl ? (React.createElement(ArrowLeftIcon, { size: "small", styles: { margin: "0 .4rem" } })) : (React.createElement(ArrowRightIcon, { size: "small", styles: { margin: "0 .4rem" } })))) : link.hasOwnProperty("actionId") ? (React.createElement(React.Fragment, null,
                            React.createElement(Button, { text: true, size: "small", content: React.createElement(Text, { color: "brand" },
                                    link.title
                                        ? getText(t.locale, link.title)
                                        : t["view more"],
                                    rtl ? (React.createElement(ArrowLeftIcon, { size: "small", styles: { margin: "0 .4rem" } })) : (React.createElement(ArrowRightIcon, { size: "small", styles: { margin: "0 .4rem" } }))), onClick: function () {
                                    return onInteraction &&
                                        onInteraction({
                                            event: "click",
                                            target: "widget",
                                            widget: id,
                                            subject: link.actionId,
                                        });
                                }, variables: function (_a) {
                                    var colorScheme = _a.colorScheme;
                                    return ({
                                        color: colorScheme.brand.foreground,
                                    });
                                } }))) : null)))));
            } })));
};
var EmptyState = function (_a) {
    var borderColor = _a.borderColor;
    return (React.createElement(Box, { styles: {
            height: "100%",
            border: "1px dashed ".concat(borderColor),
        } }));
};
//# sourceMappingURL=DashboardWidget.js.map